import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ServiceAppointmentService } from '../../../../core/data-services/service-appointment.service';
import { WebSocketService } from '../../../../core/services/websocket.service';
import { ToastService } from '../../../../core/services/toast.service';
import { ApiError } from '../../../../abstract/api-error';
import { ServiceAppointment } from '../../../../core/data-models/service-appointment.model';
import { SERVICE_APPOINTMENT_STATUSES, PREFERRED_TIME_OPTIONS } from '../../../../shared/constants/service-appointment-options';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

@Component({
  selector: 'app-service-appointment-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, MaxLengthDirective, CharCounterDirective],
  templateUrl: './service-appointment-detail.component.html',
  styleUrls: ['./service-appointment-detail.component.scss']
})
export class ServiceAppointmentDetailComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private appointmentId: string | null = null;

  appointment: ServiceAppointment | null = null;
  isLoading = false;
  isCancelling = false;
  showCancelForm = false;
  cancelReason = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private appointmentService: ServiceAppointmentService,
    private websocketService: WebSocketService,
    private toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.appointmentId = this.route.snapshot.paramMap.get('id');
    if (this.appointmentId) {
      this.loadAppointment(this.appointmentId);
      this.subscribeToWebSocketEvents();
    } else {
      this.goBack();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToWebSocketEvents(): void {
    // Listen for updates to this specific appointment
    this.websocketService.on<{
      appointmentId: string;
      status: string;
      droneName: string;
      preferredDate: string;
      technicianName: string | null;
      scheduledDate: string | null;
      timestamp: string;
    }>('appointment.updated').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      if (event.appointmentId === this.appointmentId) {
        this.loadAppointment(this.appointmentId!);
        this.toastService.info('Appointment details updated', {
          title: 'Update Received',
          duration: 4000,
          icon: 'bi-calendar-check'
        });
      }
    });

    // Listen for status changes on this specific appointment
    this.websocketService.on<{
      appointmentId: string;
      oldStatus: string;
      newStatus: string;
      droneName: string;
      timestamp: string;
    }>('appointment.statusChanged').pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      if (event.appointmentId === this.appointmentId) {
        this.loadAppointment(this.appointmentId!);
        const statusText = this.getStatusText(event.newStatus);
        this.toastService.info(`Status changed to ${statusText}`, {
          title: 'Status Update',
          duration: 5000,
          icon: 'bi-calendar-check'
        });
      }
    });
  }

  async loadAppointment(id: string): Promise<void> {
    this.isLoading = true;
    try {
      this.appointment = await this.appointmentService.getAppointment(id);
      if (!this.appointment) {
        this.toastService.error('Appointment not found');
        this.goBack();
      }
    } catch (error) {
      console.error('Error loading appointment:', error);
      this.toastService.error('Failed to load appointment');
      this.goBack();
    } finally {
      this.isLoading = false;
    }
  }

  get isCancellable(): boolean {
    if (!this.appointment) return false;
    return ['requested', 'scheduled', 'confirmed'].includes(this.appointment.status);
  }

  toggleCancelForm(): void {
    this.showCancelForm = !this.showCancelForm;
    if (!this.showCancelForm) {
      this.cancelReason = '';
    }
  }

  async confirmCancel(): Promise<void> {
    if (!this.appointment || !this.cancelReason.trim()) return;

    this.isCancelling = true;
    try {
      await this.appointmentService.cancelAppointment(this.appointment.id, this.cancelReason.trim());
      this.toastService.success('Appointment cancelled successfully');
      this.goBack();
    } catch (error: unknown) {
      this.toastService.error(ApiError.fromHttpError(error).errors[0] ?? 'Failed to cancel appointment');
    } finally {
      this.isCancelling = false;
    }
  }

  getStatusBadgeClass(status: string): string {
    const statusInfo = SERVICE_APPOINTMENT_STATUSES.find(s => s.value === status);
    if (!statusInfo) return 'bg-secondary';
    if (statusInfo.color === 'warning') return 'bg-warning text-dark';
    return `bg-${statusInfo.color}`;
  }

  getStatusText(status: string): string {
    const statusInfo = SERVICE_APPOINTMENT_STATUSES.find(s => s.value === status);
    return statusInfo?.label || status;
  }

  getPreferredTimeLabel(value: string | null | undefined): string {
    const option = PREFERRED_TIME_OPTIONS.find(o => o.value === value);
    return option?.label || value || '';
  }

  formatDate(dateString: string | Date | null | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  formatDateTime(dateString: string | Date | null | undefined): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });
  }

  goBack(): void {
    this.router.navigate(['/maintenance/service-appointments']);
  }
}
